import React, { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, Check, Plus, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, setBearerToken } from "../../lib/api";
import { useAuth } from "../../data/authStore";
import { roleLabel } from "../../lib/roles";

/**
 * OrgSwitcher — sidebar dropdown showing all orgs the user belongs to.
 * Clicking another org POSTs /api/auth/switch-org, swaps the JWT, and
 * reloads the app so all org-scoped data re-hydrates from the new context.
 */
export const OrgSwitcher = ({ collapsed = false, inline = false }) => {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(null);
  const ref = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/auth/orgs");
      setOrgs(data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.id, user?.org_id]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = orgs.find((o) => o.is_active) || {
    org_id: user?.org_id,
    org_name: "Workspace",
    role: user?.role,
    role_label: roleLabel(user?.role),
  };

  const onSwitch = async (orgId) => {
    if (orgId === active.org_id || switching) return;
    setSwitching(orgId);
    try {
      const { data } = await api.post("/api/auth/switch-org", { org_id: orgId });
      // The tab's bearer token takes precedence over the cookie — swap it or the old workspace reloads.
      if (data?.access_token) setBearerToken(data.access_token);
      toast.success(`Switched to ${data.org_name || "workspace"}`);
      // Hard reload so chainStore + dashboard refetch under the new JWT.
      window.location.assign("/dashboard");
    } catch (err) {
      toast.error("Couldn't switch workspace", { description: err?.response?.data?.detail || err.message });
      setSwitching(null);
    }
  };

  return (
    <div ref={ref} className="relative" data-testid="org-switcher">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); if (!orgs.length) load(); }}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.08] hover:border-white/[0.16] transition-all ${collapsed ? "justify-center" : ""}`}
        data-testid="org-switcher-trigger"
        title={collapsed ? active.org_name : undefined}
      >
        <span className="w-7 h-7 shrink-0 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-white text-[10.5px] font-bold flex items-center justify-center">
          {(active.org_name || "C").slice(0, 1).toUpperCase()}
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 min-w-0 text-left">
              <span className="block text-[12.5px] font-semibold text-slate-100 truncate">{active.org_name}</span>
              <span className="block text-[10px] text-slate-400 truncate">{active.role_label}</span>
            </span>
            <ChevronsUpDown size={13} className="text-slate-400 shrink-0" />
          </>
        )}
      </button>

      {open && !collapsed && (
        <div
          className={`${inline ? "relative" : "absolute left-0 right-0 z-50"} mt-2 rounded-xl bg-[#0F0E1A] border border-white/[0.08] shadow-[0_18px_44px_-12px_rgba(0,0,0,0.6)] overflow-hidden`}
          data-testid="org-switcher-menu"
        >
          <div className="px-3 py-2 border-b border-white/[0.06]">
            <p className="text-[9.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Workspaces</p>
          </div>
          <div className="max-h-[260px] overflow-y-auto cf-scroll-dark">
            {loading && (
              <div className="px-3 py-4 flex items-center gap-2 text-slate-400 text-[12px]">
                <Loader2 size={12} className="animate-spin" /> Loading workspaces…
              </div>
            )}
            {!loading && orgs.length === 0 && (
              <div className="px-3 py-4 text-slate-500 text-[12px]">No workspaces yet.</div>
            )}
            {orgs.map((o) => (
              <button
                key={o.org_id}
                type="button"
                onClick={() => onSwitch(o.org_id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/[0.06] text-left transition-colors ${o.is_active ? "bg-white/[0.05]" : ""}`}
                data-testid={`org-switcher-item-${o.org_id}`}
              >
                <span className="w-7 h-7 shrink-0 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-white text-[10.5px] font-bold flex items-center justify-center">
                  {(o.org_name || "C").slice(0, 1).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[12.5px] font-semibold text-white truncate">{o.org_name}</span>
                  <span className="block text-[10px] text-slate-400 truncate">{o.role_label} · {o.member_count} member{o.member_count === 1 ? "" : "s"}</span>
                </span>
                {switching === o.org_id ? (
                  <Loader2 size={13} className="text-indigo-300 animate-spin" />
                ) : o.is_active ? (
                  <Check size={13} className="text-indigo-300" />
                ) : null}
              </button>
            ))}
          </div>
          <div className="border-t border-white/[0.06]">
            <button
              type="button"
              onClick={() => { setOpen(false); refresh?.(); toast("Create another workspace coming soon"); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/[0.06] text-left text-[12px] text-slate-300 transition-colors"
              data-testid="org-switcher-new"
            >
              <span className="w-7 h-7 shrink-0 rounded-lg bg-white/[0.06] border border-white/[0.08] text-slate-300 flex items-center justify-center">
                <Plus size={13} />
              </span>
              <span className="flex-1">Create new workspace</span>
              <Building2 size={11} className="text-slate-500" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
